import * as moment from "moment";
import { Cycle, Pregnancy } from "../models/period";
import { UserProfile } from "../models/user";

function beginningOfLocalDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatDate(date?: Date | null): string {
  if (!date) return "";
  const d = beginningOfLocalDay(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export class UserDataFormatter {
  static generateHeader(type: "cycle" | "pregnancy" | "event" | "userInfo"): string {
    switch (type) {
      case "cycle":
        return "Cycles/Periods ";
      case "pregnancy":
        return "Pregnancies ";
      case "event":
        return "Symptoms ";
      case "userInfo":
        return "User info ";
    }
  }

  static formatDate(date?: Date | null): string {
    return formatDate(date);
  }

  static formatCurrentDay(date: Date): string {
    return `Today: ${formatDate(date)}; `;
  }

  static formatUserContraception(contraception:string[]): string {
    if (!contraception.length) return "";
    return `Contraception methods: ${contraception.join(", ")}; `;
  }

  static formatUserData(user: {
    firstName: string;
    lastName: string;
    dateOfBirth: Date;
    weight: { stringRepresentation(units: any): string };
    height: { stringRepresentation(units: any): string };
    units: any;
    dietPreference: string;
  }): string {
    const surname = user.lastName ? `surname: ${user.lastName};` : "";
    const yearOfBirth =
      beginningOfLocalDay(user.dateOfBirth).getTime() !== beginningOfLocalDay(new Date()).getTime()
        ? `year of birth: ${user.dateOfBirth.getFullYear()};`
        : "";
    return (
      this.generateHeader("userInfo") +
      `name: ${user.firstName}; ${surname} ${yearOfBirth} weight: ${user.weight.stringRepresentation(
        user.units
      )}; height: ${user.height.stringRepresentation(user.units)}; diet: ${user.dietPreference};`
    );
  }

  static formatUserDataFromProfile(userProfile: UserProfile): string {
    const userName = userProfile.name ?? "";
    const firstName = userName.split(" ")[0] ?? "";
    const nameData = firstName ? `name: ${firstName};` : "";

    const yearOfBirth =
      userProfile.dateOfBirth && userProfile.dateOfBirth.split("-").length > 0
        ? `year of birth: ${userProfile.dateOfBirth.split("-")[0]};`
        : "";

    const weightData =
      userProfile.weightInKg && userProfile.weightInKg > 0
        ? `weight: ${userProfile.weightInKg.toFixed(2)} kg;`
        : "";

    const heightData =
      userProfile.heightInCm && userProfile.heightInCm > 0
        ? `height: ${userProfile.heightInCm.toFixed(2)} cm;`
        : "";

    return this.generateHeader("userInfo") + `${nameData} ${yearOfBirth} ${weightData} ${heightData} `;
  }

  static formatPregnancy(pregnancy: Pregnancy

  ): string {
    let isPregnancyActive = pregnancy.endDate == null;
    if (!pregnancy.endDate && isPregnancyActive) {
      const week = this.pregnancyWeek(pregnancy);
      return `current pregnancy start: ${pregnancy.startDate}; current pregnancy due date: ${pregnancy.dueDate
        }; current pregnancy week: ${week}; `;
    }
    return `pregnancy start: ${pregnancy.startDate}; pregnancy end: ${pregnancy.endDate
      }; `;
  }

  static formatCycle(
    cycle: Cycle,
    inPregnancyOrPostPartum: boolean,
  ): string {
    switch (cycle.type) {
      case "past":
        return `cycle start: ${cycle.startedOn}; cycle end: ${cycle.endedOn};`;
      case "current":
      case "predicted": {
        const cycleType = cycle.type === "current" ? "current" : "next";
        return inPregnancyOrPostPartum ? "" : `${cycleType} cycle start: ${cycle.startedOn};`;
      }
    }
  }

  static formatEvent(event: {
    date: Date;
    moods?: { title: string }[];
    symptoms?: { title: string }[];
    contraception?: { title: string }[];
    sex?: { title: string }[];
    flow?: { title: string } | null;
    discharge?: { title: string } | null;
  }): string {
    const fmt = (arr?: { title: string }[] | null) =>
      arr && arr.length ? arr.map(e => e.title).join(", ") : "";

    const moods = fmt(event.moods);
    const symptoms = fmt(event.symptoms);
    const contr = fmt(event.contraception);
    const sex = fmt(event.sex);
    const flow = event.flow ? `Menstruation Flow: ${event.flow.title}` : "";
    const discharge = event.discharge ? `Discharge: ${event.discharge.title}` : "";

    const data = [moods, symptoms, contr, sex, flow, discharge].filter(Boolean).join(", ");
    return `${formatDate(event.date)}: {${data}};`;
  }

  static formatAdditionalReproductiveHealthData(args: {
    cycleLength: number;
    shouldTrackFertileWindow: boolean;
    currentCycleData: string;
    predictedCycleData: string;
    cycles: Cycle[],
    inPregnancyOrPostPartum: boolean,
    cyclePhase: string,
    usageReason: string | null,
  }): string {
    const { cycleLength, shouldTrackFertileWindow, cycles, currentCycleData, predictedCycleData, inPregnancyOrPostPartum, cyclePhase, usageReason } = args;

    let data = ` cycle average: ${cycleLength}; `;
    let localCyclePhase = cyclePhase;
    const currentDate = beginningOfLocalDay(new Date());
    const currentCycle = cycles.find((c: any) => c.type == 'current');
    const isOvulatoryCycle = UserDataFormatter.isCycleOvulatory(currentCycle);

    if (isOvulatoryCycle && shouldTrackFertileWindow && !inPregnancyOrPostPartum) {
      let nextOvulationDay: Date | null = null;
      const nextPredicted = cycles.find(
        (c: any) =>
          c.type === 'predicted' &&
          moment(c.startedOn, 'YYYY-MM-DD', true).isSameOrAfter(moment(currentDate).startOf('day'))
      );

      if (nextPredicted) {
        const ovulationYMD = moment(nextPredicted.startedOn, "YYYY-MM-DD", true)
          .subtract(14, "day")
          .toDate();

        if (ovulationYMD >= currentDate) {
          nextOvulationDay = ovulationYMD;
        } else {
          const nextPredicted = cycles.find(
            (c: any) =>
              c.type === 'predicted' &&
              moment(c.startedOn, 'YYYY-MM-DD', true).isSameOrAfter(moment(currentDate).startOf('day'))
          );

          nextOvulationDay = nextPredicted
            ? (
              (nextPredicted.endedOn
                ? moment(nextPredicted.endedOn, 'YYYY-MM-DD', true)
                : moment(nextPredicted.startedOn, 'YYYY-MM-DD', true).add(cycleLength, 'day')
              )
                .subtract(14, 'day')
                .startOf('day')
                .toDate()
            )
            : null;
        }
      }
      if (nextOvulationDay) data += `next ovulation day: ${formatDate(nextOvulationDay)}; `;
    }

    data += currentCycleData + " ";
    data += predictedCycleData + " ";

    if ((!isOvulatoryCycle || !shouldTrackFertileWindow) &&
      localCyclePhase !== "Period" &&
      localCyclePhase !== "Luteal") {
      localCyclePhase = "Luteal";
    }

    const phase = localCyclePhase == "Pregnancy"
      ? "pregnancy"
      : localCyclePhase == "Postpartum"
        ? "postpartum"
        : localCyclePhase;

    data += `current cycle phase: ${phase}; `;

    if (!inPregnancyOrPostPartum) {
      const ovulatory = isOvulatoryCycle && shouldTrackFertileWindow
        ? "current cycle is ovulatory;"
        : "current cycle is non-ovulatory;";
      data += ovulatory + " ";
    }

    data += usageReason != null && usageReason?.length > 0 ? `User's reason for using the app: ${usageReason}; ` : '';

    return data;
  }

  static pregnancyWeek(pregnancy: Pregnancy): number {
    const today = moment().startOf("day");
    const start = moment(pregnancy.startDate, "YYYY-MM-DD", true).startOf("day");
    if (!start.isValid()) return 0;

    const days = today.diff(start, "days") + 1;
    return Math.floor(days / 7);
  }

  static isCycleOvulatory(
    cycle: Cycle | null,
    defaultLength = 28
  ): boolean {
    if (cycle == null || cycle.startedOn == null) {
      return true;
    }

    const start = moment(cycle.startedOn, "YYYY-MM-DD", true);
    const end = cycle.endedOn
      ? moment(cycle.endedOn as string, "YYYY-MM-DD", true)
      : start.clone().add(defaultLength - 1, "day");

    // By default set ovulatory
    if (!start.isValid() || !end.isValid()) return true;

    const days = end.diff(start, "days") + 1;
    return days >= 21 && days <= 35;
  }
}
